import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { EmissionTier, MetricState, AdaptationView } from '../../common'
import { usePropertyClimateData, isActiveMetricState } from '../../../../hooks/climateData'
import type { MetricDataFilters } from '../../../../hooks/analytics'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import ClimateComputationIndicator from '../../common/ClimateComputationIndicator'
import MetricStatePlaceholder from '../../common/MetricStatePlaceholder'
import {
  collectEventMetricByReturnPeriod,
  formatCurrencyValue,
  formatPercentValue,
  formatPlainNumber,
} from '../../common/financialMetrics'
import { useCurrentUser } from '../../../../hooks/users'

type MetricUnit = 'currency' | 'ratio' | 'days'

type MetricOption = {
  key: string
  label: string
  unit: MetricUnit
  aggregation: 'sum' | 'mean'
}

const METRIC_OPTIONS: MetricOption[] = [
  {
    key: 'fld:damage_loss',
    label: 'Losses from Damages',
    unit: 'currency',
    aggregation: 'sum',
  },
  {
    key: 'fld:bi_loss',
    label: 'Losses from Business Interruption',
    unit: 'currency',
    aggregation: 'sum',
  },
  {
    key: 'fld:damage_ratio',
    label: 'Damage Ratio',
    unit: 'ratio',
    aggregation: 'mean',
  },
  {
    key: 'fld:bi_days',
    label: 'Business Interruption Days',
    unit: 'days',
    aggregation: 'sum',
  },
]

const DEFAULT_METRIC_KEY = 'fld:damage_loss'

export default function FloodEventMetrics({
  propertyId,
  emissionTier,
  filters,
  view,
}: {
  propertyId: number
  emissionTier: EmissionTier
  filters?: MetricDataFilters | null
  view?: AdaptationView
}) {
  const { currencySymbol } = useCurrentUser()
  const [selectedMetricKey, setSelectedMetricKey] = useState(DEFAULT_METRIC_KEY)

  const selectedMetric = METRIC_OPTIONS.find(m => m.key === selectedMetricKey) ?? METRIC_OPTIONS[0]
  const floodQueryParams = useMemo(() => ({ view, ...(filters ? { filters } : {}) }), [filters, view])

  const { data, isLoading, isFetching, error } = usePropertyClimateData(
    selectedMetricKey,
    { propertyId },
    floodQueryParams,
  )
  const loading = isLoading || isFetching

  const formatValue = useMemo(() => {
    switch (selectedMetric.unit) {
      case 'currency':
        return (v: number) => formatCurrencyValue(v, currencySymbol, 0)
      case 'ratio':
        return (v: number) => formatPercentValue(v, 1)
      case 'days':
        return (v: number) => formatPlainNumber(v, 1)
    }
  }, [selectedMetric.unit, currencySymbol])

  const { valueByRpAndYear, returnPeriods, years } = useMemo(() => {
    const empty = {
      valueByRpAndYear: {} as Record<string, Record<number, number>>,
      returnPeriods: [] as string[],
      years: [] as number[],
    }
    if (!data || data.state?.value !== MetricState.SUCCEEDED || !data.data?.data?.[emissionTier]) {
      return empty
    }

    const tierData = data.data.data[emissionTier]
    const byRp = collectEventMetricByReturnPeriod(tierData, selectedMetric.aggregation)

    const keysFromMeta: string[] = data.data?.keys?.return_period ?? []
    const rps = (keysFromMeta.length ? keysFromMeta : Object.keys(byRp)).slice().sort((a, b) => Number(a) - Number(b))

    const yearSet = new Set<number>()
    Object.values(byRp).forEach(byYear => Object.keys(byYear).forEach(y => yearSet.add(Number(y))))
    const sortedYears = Array.from(yearSet).sort((a, b) => a - b)

    return { valueByRpAndYear: byRp, returnPeriods: rps, years: sortedYears }
  }, [data, emissionTier, selectedMetric.aggregation])

  const metricSelector = (
    <TextField
      select
      size="small"
      label="Metric"
      value={selectedMetricKey}
      onChange={e => setSelectedMetricKey(e.target.value)}
      sx={{ minWidth: 240 }}>
      {METRIC_OPTIONS.map(option => (
        <MenuItem key={option.key} value={option.key}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  )

  const renderBody = () => {
    if (isLoading && !data) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={100}>
          <CircularProgress size={24} />
        </Box>
      )
    }

    if (error) {
      return <Alert severity="error">Could not load event impact data.</Alert>
    }

    if (data && data.state?.value !== MetricState.SUCCEEDED) {
      return isActiveMetricState(data.state.value) ? (
        <ClimateComputationIndicator variant="block" minHeight={100} />
      ) : (
        <MetricStatePlaceholder state={data.state.value} minHeight={100} />
      )
    }

    if (!returnPeriods.length || !years.length) {
      return <Alert severity="info">No event impact data available for this property.</Alert>
    }

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  background: 'background.paper',
                  bgcolor: 'background.paper',
                  zIndex: 1,
                  whiteSpace: 'nowrap',
                  pr: 3,
                }}>
                Return period
              </TableCell>
              {years.map(year => (
                <TableCell key={year} align="right" sx={{ whiteSpace: 'nowrap', minWidth: 90 }}>
                  {year}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {returnPeriods.map(rp => (
              <TableRow key={rp}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    whiteSpace: 'nowrap',
                    pr: 3,
                  }}>
                  {rp}-year event
                </TableCell>
                {years.map(year => {
                  const raw = valueByRpAndYear[rp]?.[year] ?? null
                  return (
                    <TableCell key={year} align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {raw != null ? formatValue(raw) : '—'}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )
  }

  return (
    <Box>
      <Stack
        justifyContent="space-between"
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        gap={2}>
        <ClimateSectionTitle
          title="Event Impacts"
          subtitle="Projected financial and operational impacts for a single flood event by return period"
          helpText="Values are summed across flood sources and targets. Rows show different event likelihoods; columns show projection years."
          loading={loading}
        />
        {metricSelector}
      </Stack>

      {renderBody()}
    </Box>
  )
}
